import { useMutation, useQuery } from "@apollo/client";
import Edit from "@mui/icons-material/Edit";
import Refresh from "@mui/icons-material/Refresh";
import { Box, Button, Tooltip, useTheme, useMediaQuery } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { getFullName } from "encamp-shared/src/utils/name";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import { Permission, UserStatus, UsersQuery } from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useTenant } from "hooks/useTenant";
import GET_ROLES_QUERY from "queries/getRoles";
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { AddUserDialog } from "./AddUserDialog";
import { BulkReassignRoleDialog } from "./BulkReassignRoleDialog";
import { EditUserDialog } from "./EditUserDialog";

export type UserRow = UsersQuery["users"]["items"][number];

// Staff can resend an invite to any user who was invited but has not yet
// completed sign-in. We intentionally don't gate on PENDING alone: the DB
// status can drift (e.g. a tenant admin toggling ACTIVE/INACTIVE on a user who
// never signed in — ENG-15863), which would otherwise hide the recovery path.
// DISABLED users are intentionally deactivated and NOT_INVITED users were never
// sent an invite, so both are excluded. The backend (resendInvitationToUser) is
// the source of truth and returns a clear error if the user is already
// confirmed in Cognito.
const RESENDABLE_USER_STATUSES: UserStatus[] = [
  UserStatus.Pending,
  UserStatus.Active,
  UserStatus.Inactive,
];

export const prettyPrintUserStatus = (userStatus: UserStatus) => {
  switch (userStatus) {
    case UserStatus.Pending:
      return "Invite Sent";
    case UserStatus.Disabled:
      return "Deactivated";
    default:
      return prettyPrintEnumValue(userStatus);
  }
};

const USERS = gql(`
  query Users($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    users(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        email
        status
        facilities {
            id
        }
        person {
          first
          last
        }
        UserTenant {
          tenantId
            role {
                name
                permissions
            }
        }
      }
      count
    }
  }
`);

const RESEND_INVITE = gql(/* GraphQL */ `
  mutation ResendInvite($email: String!) {
    resendInvitationToUser(email: $email)
  }
`);

export function Users() {
  const { tenantId, tenant } = useTenant();
  const { hasPermissions } = useAuthorization();
  const { isStaff } = useCurrentUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const canViewUsersList = hasPermissions([Permission.ReadAllUserTenant]);
  const navigate = useNavigate();
  if (!canViewUsersList) {
    navigate("/");
  }

  const [addUser, setAddUser] = useState<boolean>(false);
  const [editUser, setEditUser] = useState<UserRow | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<UserRow[]>([]);
  const [bulkReassignOpen, setBulkReassignOpen] = useState(false);

  // Fuel the role filter dropdown with this tenant's actual roles.
  const { data: rolesData } = useQuery(GET_ROLES_QUERY, {
    variables: { tenantId: tenantId ?? "" },
    skip: !tenantId,
  });
  const roleNames = useMemo(
    () => (rolesData?.roles ?? []).map((r) => r.name).sort((a, b) => a.localeCompare(b)),
    [rolesData?.roles]
  );

  useBreadcrumb([
    { label: tenant?.name ?? "Unknown Tenant" },
    { label: "Account" },
    { label: "Users" },
  ]);
  const alerts = useAlerts();
  const [resendInvite] = useMutation(RESEND_INVITE, {
    onError: (error) => alerts.error(error.message),
    onCompleted: (result) => {
      if (result.resendInvitationToUser) {
        alerts.success("Invite sent successfully.");
      }
    },
  });

  const columns: OmnisearchGridColDef<UserRow>[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: isMobile ? 0.4 : 2,
        sortable: true,
        valueGetter: (_, row) => getFullName(row.person),
      },
      {
        field: "email",
        headerName: "Email",
        flex: isMobile ? 0.45 : 2,
        sortable: true,
      },
      {
        field: "numFacilities",
        headerName: "Facilities",
        flex: isMobile ? 0.15 : 1,
        sortable: true,
        valueGetter: (_, row) => {
          const role = row.UserTenant.find((ut) => ut.tenantId === tenantId)?.role;

          if (
            role?.permissions?.includes(Permission.ReadAllFacility) ||
            role?.permissions?.includes(Permission.WriteAllFacility)
          )
            return "all";

          return row.facilities.length;
        },
      },
      {
        field: "role",
        headerName: "Role",
        flex: 2,
        sortable: false,
        filterKeyType: "enumAutocomplete",
        enumValues: roleNames,
        valueGetter: (_, row) =>
          row.UserTenant?.map((userTenant) => userTenant.role?.name).join(", "),
      },
      {
        field: "status",
        headerName: "Status",
        flex: 2,
        sortable: true,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(UserStatus),
        enumPresentationFunction: (v) => prettyPrintUserStatus(v as UserStatus),
        valueGetter: (_, row) => prettyPrintUserStatus(row.status),
      },
      {
        field: "actions",
        type: "actions",
        minWidth: 80,
        flex: 0.5,
        align: "right",
        getActions: ({ row }) => {
          const actions = [];

          if (isStaff && RESENDABLE_USER_STATUSES.includes(row.status)) {
            actions.push(
              <Tooltip title="Resend Invite" key={1}>
                <GridActionsCellItem
                  onClick={() => resendInvite({ variables: { email: row.email } })}
                  label="Resend Invite"
                  icon={<Refresh />}
                />
              </Tooltip>
            );
          }
          if (hasPermissions([Permission.WriteAllUserTenant])) {
            actions.push(
              <Tooltip title="Edit User" key={2}>
                <GridActionsCellItem
                  onClick={() => setEditUser(row)}
                  label="Edit User"
                  icon={<Edit />}
                />
              </Tooltip>
            );
          }

          return actions;
        },
      },
    ],
    [isMobile, tenantId, isStaff, hasPermissions, resendInvite, roleNames]
  );

  return (
    <Box sx={{ pt: 3 }}>
      {addUser && (
        <AddUserDialog open={addUser} onClose={() => setAddUser(false)} tenantId={tenantId ?? ""} />
      )}
      {editUser && (
        <EditUserDialog
          open={!!editUser}
          initialUserId={editUser.id}
          onClose={() => setEditUser(null)}
          tenantId={tenantId ?? ""}
        />
      )}

      {bulkReassignOpen && (
        <BulkReassignRoleDialog
          selectedUsers={selectedUsers}
          tenantId={tenantId ?? ""}
          onClose={() => setBulkReassignOpen(false)}
          onSaved={() => {
            setBulkReassignOpen(false);
            setSelectedUsers([]);
          }}
        />
      )}

      <OmnisearchDataGrid
        persistenceKey="users-table"
        isURLDriven
        columns={columns}
        dataQuery={USERS}
        excludeFilterColumns={["numFacilities", "name"]}
        checkboxSelection={hasPermissions([Permission.WriteAllUserTenant])}
        disableRowSelectionOnClick
        onSelectedRowsChanged={setSelectedUsers}
        onRowClick={(params) => setEditUser(params.row)}
        additionalFilterColumns={[
          {
            key: "first",
            header: "First Name",
          },
          {
            key: "last",
            header: "Last Name",
          },
          {
            key: "facility",
            header: "Facility Name",
            filterKeyType: "facility",
          },
        ]}
        initialSortModel={[{ field: "name", sort: "asc" }]}
        getItems={(data) => data.users.items}
        getCount={(data) => data.users.count}
        noDataMessage="No users have been added to this organization yet."
        initialPageSize={50}
        columnVisibilityModel={{
          ...(isMobile && {
            role: false,
            status: false,
            actions: false,
          }),
        }}
        commandButtons={[
          ...(hasPermissions([Permission.WriteAllUserTenant])
            ? [
                <Box key="reassign">
                  <Button
                    variant="outlined"
                    onClick={() => setBulkReassignOpen(true)}
                    disabled={selectedUsers.length === 0}
                  >
                    {selectedUsers.length > 0
                      ? `Reassign role (${selectedUsers.length})`
                      : "Reassign role"}
                  </Button>
                </Box>,
              ]
            : []),
          ...(hasPermissions([Permission.CreateUserTenant])
            ? [
                <Box key="add">
                  <Button variant="contained" onClick={() => setAddUser(true)}>
                    Add User
                  </Button>
                </Box>,
              ]
            : []),
        ]}
      />
    </Box>
  );
}
